<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'

    import { copyToClipboard } from '@common/Common'
    import { guid } from '@common/Guid'
    import { ProjectTemplateLink } from '@Projects/Model'

    import { ProjectTemplateLinkReadModel } from '@Projects/Domain/readmodels'
    import type { ProjectTemplateLinksManagerViewModel } from '@Projects/Components/ProjectTemplateLinksManager'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectTemplateLinksManagerViewModel
    }>()

    const emit = defineEmits<{
        (e: 'update-links', projectId: number | string | null, data: ProjectTemplateLink[]): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- local state ----------------

    const newLinkName = ref<string | null>(null)

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const initialized = computed(() => vm.value.initialized)
    const updating = computed(() => vm.value.updating)
    const projectName = computed(() => vm.value.projectName)
    const error = computed(() => vm.value.error)

    const links = computed<ProjectTemplateLinkReadModel[]>(() =>
        vm.value.data?.Links ?? []
    )

    // ---------------- methods ----------------

    function deleteLink(l: ProjectTemplateLinkReadModel)
    {
        const list = vm.value.data?.Links
        if (!list) return

        const index = list.indexOf(l)
        if (index >= 0)
            list.splice(index, 1)
    }

    function copyAccessUrlToClipboard(rm: ProjectTemplateLinkReadModel)
    {
        if (rm.Id <= 0)
            return

        copyToClipboard(rm.AccessUrl)

        const original = rm.AccessUrl
        rm.AccessUrl = 'Copied to clipboard'

        const handle = setTimeout(() =>
        {
            rm.AccessUrl = original
            clearTimeout(handle)
        }, 500)
    }

    function createLink()
    {
        if (!vm.value.data)
            return

        const rm = new ProjectTemplateLinkReadModel()
        rm.AccessKey = guid()
        rm.Views = 0
        rm.Downloads = 0
        rm.Id = 0
        rm.Name = newLinkName.value

        vm.value.data.Links.push(rm)
        newLinkName.value = null
    }

    function handleUpdateLinks()
    {
        vm.value.updating = true

        const data = links.value
            .filter(rm => rm.Name)
            .map(rm =>
            {
                const l = new ProjectTemplateLink()
                l.ProjectId = vm.value.projectId
                l.AccessKey = rm.AccessKey
                l.Name = rm.Name
                l.ProjectTemplateLinkId = rm.Id
                l.Views = rm.Views
                l.Downloads = rm.Downloads
                l.Created = rm.Created
                l.Modified = rm.Modified
                return l
            })

        emit('update-links', vm.value.projectId, data)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage Shareable Project Template Links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">

                        <h4 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h4>

                        <div v-if="initialized">
                            <div class="alert alert-info">
                                Share your project '<strong>{{ projectName }}</strong>' as a template using web links. Start by assigning each link a descriptive name to track its purpose.
                                After naming your links, click 'Save'. The links will then be automatically generated and ready to share.
                            </div>

                            <div class="input-group my-2">
                                <input type="text" v-model.trim="newLinkName" class="form-control" placeholder="new link name...">
                                <div class="input-group-append">
                                    <button class="btn btn-outline-secondary" type="button" @click="createLink">Create link</button>
                                </div>
                            </div>

                            <table v-if="links.length > 0" class="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th>Link name</th>
                                        <th>Url (click to copy)</th>
                                        <th>Views / Downloads</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="l in links" :key="l.Id">
                                        <td>
                                            <input type="text" class="form-control" v-model="l.Name" placeholder="Your link name" />
                                        </td>
                                        <td>
                                            <input type="text" v-if="l.Name && l.Id > 0" @click="copyAccessUrlToClipboard(l)" v-model="l.AccessUrl" title="Click to copy" class="form-control pointer" readonly />
                                            <input type="text" v-if="l.Name && l.Id === 0" :value="'Save to generate url'" class="form-control" readonly />
                                        </td>
                                        <td>
                                            <span v-if="l.Name">
                                                {{l.Views}} / {{l.Downloads}}
                                            </span>
                                        </td>
                                        <td>
                                            <button v-if="l.Name" type="button" class="btn btn-secondary" @click="deleteLink(l)">
                                                <i class="fa fa-times" aria-hidden="true"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="error">{{error}}</div>
                        </div>
                    </div>
                    <div class="modal-footer text-right">
                        <button type="button" :disabled="updating" @click="handleUpdateLinks" class="btn btn-primary">
                            {{ updating ? 'Saving...' : 'Save' }}
                        </button>
                        <button type="button" :disabled="updating" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

